import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AssignmentRecipientGroup,
  AssignmentRecipientOptions,
  AssignmentRecipientStudent
} from '../../schools/models/assignment-recipient-options.model';
import { AssignmentRecipientOptionsService } from '../../schools/services/assignment-recipient-options.service';
import { StudentGroupSelectionItem } from '../models/student-group-selection.model';

@Injectable({ providedIn: 'root' })
export class StudentGroupSelectionRepositoryService {
  constructor(private assignmentRecipientOptionsService: AssignmentRecipientOptionsService) { }

  getSelectionItems(flightSchoolId: number, courseId?: number): Observable<StudentGroupSelectionItem[]> {
    if (!flightSchoolId) {
      return of([]);
    }

    return this.assignmentRecipientOptionsService.getOptions()
      .pipe(map(options => this.mapSelectionItems(options, courseId)));
  }

  getStudentIdsForGroups(flightSchoolId: number, flightSchoolGroupIds: number[], courseId?: number): Observable<number[]> {
    const groupIds = (flightSchoolGroupIds || []).filter(groupId => !!groupId);

    if (!flightSchoolId || groupIds.length === 0) {
      return of([]);
    }

    return this.assignmentRecipientOptionsService.getOptions()
      .pipe(map(options => {
        const eligibleStudentIds = this.getEligibleStudentIds(options.Students || [], courseId);
        const eligibleStudentIdSet = new Set(eligibleStudentIds);

        return this.distinct((options.Groups || [])
          .filter(group => groupIds.indexOf(group.FlightSchoolGroupId) >= 0)
          .reduce((studentIds, group) => studentIds.concat(group.StudentIds || []), [])
          .filter(studentId => !courseId || eligibleStudentIdSet.has(studentId)));
      }));
  }

  clearSelectionItems(): void {
    this.assignmentRecipientOptionsService.clear();
  }

  private mapSelectionItems(options: AssignmentRecipientOptions, courseId?: number): StudentGroupSelectionItem[] {
    const students = options.Students || [];
    const eligibleStudentIds = this.getEligibleStudentIds(students, courseId);
    const eligibleStudentIdSet = new Set(eligibleStudentIds);
    const groupItems = (options.Groups || [])
      .map(group => this.mapGroup(group, students, eligibleStudentIdSet, !!courseId));
    const studentItems = students
      .map(student => this.mapStudent(student, !courseId || eligibleStudentIdSet.has(student.StudentId)));

    return [...groupItems, ...studentItems];
  }

  private mapGroup(
    group: AssignmentRecipientGroup,
    students: AssignmentRecipientStudent[],
    eligibleStudentIdSet: Set<number>,
    courseSelected: boolean
  ): StudentGroupSelectionItem {
    const visibleStudentIds = this.distinct(group.StudentIds || []);
    const studentIds = courseSelected
      ? visibleStudentIds.filter(studentId => eligibleStudentIdSet.has(studentId))
      : visibleStudentIds;
    const ignoredStudentCount = visibleStudentIds.length - studentIds.length;
    const ignoredStudentNames = students
      .filter(student => visibleStudentIds.indexOf(student.StudentId) >= 0 && !eligibleStudentIdSet.has(student.StudentId))
      .map(student => student.DisplayName || `Student ${student.StudentId}`);

    return {
      id: group.FlightSchoolGroupId,
      type: 'group',
      label: group.Name || `Group ${group.FlightSchoolGroupId}`,
      studentIds,
      allStudentIds: visibleStudentIds,
      studentCount: studentIds.length,
      studentCountLabel: `${studentIds.length} valid ${studentIds.length === 1 ? 'student' : 'students'}`,
      ignoredStudentCount,
      ignoredStudentNames,
      isEligible: !courseSelected || studentIds.length > 0,
      eligibilityMessage: ignoredStudentCount > 0
        ? `${ignoredStudentCount} ${ignoredStudentCount === 1 ? 'student is' : 'students are'} not enrolled in the selected course.`
        : undefined,
      detail: ignoredStudentCount > 0
        ? `${studentIds.length} eligible, ${ignoredStudentCount} ignored`
        : `${studentIds.length} ${studentIds.length === 1 ? 'student' : 'students'}`
    };
  }

  private mapStudent(student: AssignmentRecipientStudent, isEligible: boolean): StudentGroupSelectionItem {
    return {
      id: student.StudentId,
      type: 'student',
      label: student.DisplayName || `Student ${student.StudentId}`,
      initials: this.getInitials(student.DisplayName),
      detail: isEligible ? undefined : 'Not enrolled in the selected course',
      isEligible,
      eligibilityMessage: isEligible ? undefined : 'Not enrolled in the selected course.'
    };
  }

  private getEligibleStudentIds(students: AssignmentRecipientStudent[], courseId?: number): number[] {
    return this.distinct((students || [])
      .filter(student => !courseId || (student.ActiveCourseIds || []).indexOf(courseId) >= 0)
      .map(student => student.StudentId));
  }

  private getInitials(displayName: string): string {
    return (displayName || '?').split(' ').filter(part => !!part).slice(0, 2)
      .map(part => part.charAt(0)).join('').toUpperCase();
  }

  private distinct(values: number[]): number[] {
    return values.filter((value, index, source) => source.indexOf(value) === index);
  }
}
